'use strict'

import {
  propsToMap
} from './portal-waypoint-core-utils.js'
import {
  xmlToObj
} from './portal-point-map-utils.js'
import {
  parseKmzMission
} from './portal-kmz-mission-utils.js'
import {
  showPortalPropContextMenu
} from './portal-prop-context-bridge.js'

let KMZ_WAYPOINT_SCOPE_MAP_CACHE = null

const escapePropTooltipHtml = function (rawText) {
  return String(rawText === null || rawText === undefined ? '' : rawText)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\r?\n/g, '<br>')
}

const markKmzScopeForPath = function (scopeMap, path, scopeId, fnBuildPathVariants) {
  const variants = (typeof fnBuildPathVariants === 'function') ? fnBuildPathVariants(path) : [String(path || '').trim()]
  variants.forEach((variant) => {
    const key = String(variant || '').trim()
    if (!key) return
    if (!scopeMap[key]) {
      scopeMap[key] = { template: 0, waylines: 0, unresolved: 0 }
    }
    if (scopeId === 'template') scopeMap[key].template = 1
    if (scopeId === 'waylines') scopeMap[key].waylines = 1
  })
}

const getKmzWaypointScopeMap = function (fnBuildPathVariants) {
  if (KMZ_WAYPOINT_SCOPE_MAP_CACHE) return KMZ_WAYPOINT_SCOPE_MAP_CACHE
  KMZ_WAYPOINT_SCOPE_MAP_CACHE = {}
  if (typeof document === 'undefined') return KMZ_WAYPOINT_SCOPE_MAP_CACHE
  const templateXml = String((document.querySelector('[data-tpl-new-kmz-template]') || {}).textContent || '').trim()
  const waylinesXml = String((document.querySelector('[data-tpl-new-kmz-waylines]') || {}).textContent || '').trim()
  if (!templateXml || !waylinesXml) return KMZ_WAYPOINT_SCOPE_MAP_CACHE
  try {
    const parsedMission = parseKmzMission(templateXml, waylinesXml, xmlToObj)
    const firstPoint = (parsedMission && Array.isArray(parsedMission.waypoints)) ? parsedMission.waypoints[0] : null
    if (!firstPoint || !firstPoint.xmlEl) return KMZ_WAYPOINT_SCOPE_MAP_CACHE
    const templateMap = propsToMap(xmlToObj(firstPoint.xmlEl))
    const waylinesNode = Array.isArray(firstPoint.linkedXmlEls) ? firstPoint.linkedXmlEls[0] : null
    const waylinesMap = waylinesNode ? propsToMap(xmlToObj(waylinesNode)) : {}
    Object.keys(templateMap).forEach((path) => {
      markKmzScopeForPath(KMZ_WAYPOINT_SCOPE_MAP_CACHE, path, 'template', fnBuildPathVariants)
    })
    Object.keys(waylinesMap).forEach((path) => {
      markKmzScopeForPath(KMZ_WAYPOINT_SCOPE_MAP_CACHE, path, 'waylines', fnBuildPathVariants)
    })
  } catch (_error) {}
  return KMZ_WAYPOINT_SCOPE_MAP_CACHE
}

const renderPropEditorRows = function ($container, rows, options, {
  _ = window._,
  $ = window.jQuery,
  fnT = function (key) { return String(key || '') },
  NON_EXPORT_PROJECT_FILENAME_PATH: nonExportProjectFilenamePath = 'project.filename',
  NON_EXPORT_POINT_HEIGHT_PATH: nonExportPointHeightPath = 'project.height',
  DEM_PROVIDER_PRIORITY = [],
  fnGetDemProviderLabel,
  fnRunPointHeightDemOverwriteFlow,
  fnGetPointHeightToolFieldMeta,
  fnGetPointNumericByPathAliases,
  toFiniteNumber,
  pathWithoutNamespacePrefixes,
  pathWithoutNumericParts,
  fnResolvePropPresetOptions,
  getPathLeafField,
  pathWithoutLeadingScope,
  fnResolvePropLabelTooltip,
  fnResolvePropLabelDetailedHelp,
  fnResolvePropLabelIconClass,
  fnResolvePropLabelRequired,
  fnBuildHeadingSuggestModel,
  toUnixMs,
  fnFormatUnixMsForDisplay,
  fnParseDisplayTimestampToUnixMs,
  TIMESTAMP_DISPLAY_FORMAT = 'YYYY-MM-DD HH:mm:ss',
  fnResolveI18Text,
  fnResolvePropValueHelp,
  fnShowPropContextMenu,
  PROP_FIELD_DISABLE_RULES = [],
} = {}) {
  const NON_EXPORT_PROJECT_FILENAME_PATH = String(nonExportProjectFilenamePath || 'project.filename').toLowerCase()
  const NON_EXPORT_POINT_HEIGHT_PATH = String(nonExportPointHeightPath || 'project.height').toLowerCase()
  const opts = _.assign({
    idPrefix: 'prop-edit',
    markDirty: false,
    readonlyPaths: [],
    templateDefaults: {},
    compactSharedPrefix: false,
    stripNamespacePrefixes: false,
    enableClearToggle: false,
    helpTextByPath: {},
    normalizeProjectFileName: null,
    refreshProjectFileName: null,
    pointTargets: [],
    onPointHeightToolApplied: null,
    kmzScopeContext: '',
    kmzScopeBindingsByPath: null
  }, options || {})
  const templateDefaults = (opts.templateDefaults && typeof opts.templateDefaults === 'object') ? opts.templateDefaults : {}
  const helpTextByPath = (opts.helpTextByPath && typeof opts.helpTextByPath === 'object') ? opts.helpTextByPath : {}
  const pointTargets = Array.isArray(opts.pointTargets) ? opts.pointTargets.filter((pt) => !!pt) : []
  const $groupsParent = $('<div class="container-fluid"></div>')
  const fnBuildEffectiveDemProviderKeys = function () {
    const localState = (typeof window !== 'undefined') ? window.PORTAL_LOCAL_TERRAIN_STATE : null
    const localActive = !!(
      localState &&
      localState.active === true &&
      (
        typeof localState.sampleElevation === 'function' ||
        typeof localState.sampleElevationAsync === 'function' ||
        typeof localState.sampleElevations === 'function'
      )
    )
    const out = localActive ? ['localDEM'] : []
    DEM_PROVIDER_PRIORITY.forEach((providerKey) => {
      const key = String(providerKey || '').trim()
      if (!key || out.includes(key)) return
      out.push(key)
    })
    return out
  }
  const fnBuildKmzPathVariants = function (rawPath) {
    const raw = String(rawPath || '').trim()
    if (!raw) return []
    const noNs = String(pathWithoutNamespacePrefixes(raw) || '').trim()
    const noLead = String(pathWithoutLeadingScope(noNs) || '').trim()
    const rawNoNums = String(pathWithoutNumericParts(raw) || '').trim()
    const noNsNoNums = String(pathWithoutNumericParts(noNs) || '').trim()
    const noLeadNoNums = String(pathWithoutNumericParts(noLead) || '').trim()
    return [raw, noNs, noLead, rawNoNums, noNsNoNums, noLeadNoNums]
      .filter((value, idx, arr) => !!value && arr.indexOf(value) === idx)
  }
  const kmzScopeContext = String(opts.kmzScopeContext || '').trim().toLowerCase()
  const kmzScopeBindingsByPath = (opts.kmzScopeBindingsByPath && typeof opts.kmzScopeBindingsByPath === 'object')
    ? opts.kmzScopeBindingsByPath
    : null
  const kmzWaypointScopeMap = (kmzScopeContext === 'waypoint')
    ? getKmzWaypointScopeMap(fnBuildKmzPathVariants)
    : null
  const fnResolveKmzScopeInfo = function (path) {
    const rawPath = String(path || '').trim()
    const lowerPath = rawPath.toLowerCase()
    if (!rawPath || lowerPath.indexOf('project.') === 0) return null
    if (kmzScopeBindingsByPath && Object.prototype.hasOwnProperty.call(kmzScopeBindingsByPath, rawPath)) {
      const bindings = Array.isArray(kmzScopeBindingsByPath[rawPath]) ? kmzScopeBindingsByPath[rawPath] : []
      return {
        template: bindings.some((binding) => String(binding && binding.scopeId || '').trim() === 'template') ? 1 : 0,
        waylines: bindings.some((binding) => String(binding && binding.scopeId || '').trim() === 'waylines') ? 1 : 0,
        unresolved: 0
      }
    }
    if (lowerPath.indexOf('template.') === 0) {
      return { template: 1, waylines: 0, unresolved: 0 }
    }
    if (lowerPath.indexOf('waylines.') === 0) {
      return { template: 0, waylines: 1, unresolved: 0 }
    }
    if (kmzWaypointScopeMap) {
      const variants = fnBuildKmzPathVariants(rawPath)
      for (let i = 0; i < variants.length; i += 1) {
        const variant = variants[i]
        if (!Object.prototype.hasOwnProperty.call(kmzWaypointScopeMap, variant)) continue
        return kmzWaypointScopeMap[variant]
      }
      return { template: 1, waylines: 1, unresolved: 1 }
    }
    return null
  }
  const fnBuildKmzScopeTooltip = function (scopeInfo) {
    if (!scopeInfo) return ''
    if (scopeInfo.unresolved) return fnT('kmz.scope.unresolved')
    if (scopeInfo.template && scopeInfo.waylines) return fnT('kmz.scope.both')
    if (scopeInfo.template) return fnT('kmz.scope.template_only')
    if (scopeInfo.waylines) return fnT('kmz.scope.waylines_only')
    return fnT('kmz.scope.none')
  }
  const fnResolveKmzScopeVariant = function (scopeInfo) {
    if (!scopeInfo) return ''
    if (scopeInfo.unresolved) return 'unresolved'
    if (scopeInfo.template && scopeInfo.waylines) return 'both'
    if (scopeInfo.template) return 'template'
    if (scopeInfo.waylines) return 'waylines'
    return 'none'
  }
  const fnBuildKmzScopeContextHelp = function (baseShort, baseDetailed, scopeInfo) {
    const shortBase = String(baseShort || '').trim()
    const detailedBase = String(baseDetailed || baseShort || '').trim()
    if (!scopeInfo) {
      return {
        shortHelp: shortBase,
        detailedHelp: detailedBase,
        kmzScopeVariant: ''
      }
    }
    const scopeSummary = fnBuildKmzScopeTooltip(scopeInfo)
    const detailedParts = [
      detailedBase,
      scopeSummary
    ].filter((item, idx, arr) => !!item && arr.indexOf(item) === idx)
    return {
      shortHelp: shortBase,
      detailedHelp: detailedParts.join('\n'),
      kmzScopeVariant: fnResolveKmzScopeVariant(scopeInfo)
    }
  }
  const fnHandlePropContextMenu = function (event, data) {
    showPortalPropContextMenu(event, data, fnShowPropContextMenu)
  }

  const fnAttachClearToggleIfNeeded = function ($inputWrap, $input) {
    if (!opts.enableClearToggle) return
    if (!$input || !$input.length) return
    if (String($input.attr('type') || '').toLowerCase() !== 'text') return
    if ($input.hasClass('d-none')) return
    if ($input.data('clearToggleAttached') === true) return
    const $btn = $('<button type="button" class="btn btn-outline-danger" data-prop-clear-toggle></button>')
      .attr('title', fnT('ui.clear_on_save'))
      .attr('data-bs-toggle', 'tooltip')
      .attr('data-bs-placement', 'top')
      .attr('aria-pressed', 'false')
      .html('<i class="fa-solid fa-trash"></i>')
    const fnSyncBtnState = function () {
      const active = $input.data('propClearOnSave') === true
      $btn.toggleClass('active', active)
      $btn.toggleClass('btn-danger', active)
      $btn.toggleClass('text-white', active)
      $btn.toggleClass('btn-outline-danger', !active)
      $btn.attr('aria-pressed', active ? 'true' : 'false')
    }
    let $group = $input.closest('.input-group')
    if (!$group.length) {
      $group = $('<div class="input-group input-group-sm"></div>')
      $input.before($group)
      $group.append($input)
    }
    $group.append($btn)
    $btn.on('click', function () {
      if ($input.prop('disabled')) return
      const next = !($input.data('propClearOnSave') === true)
      $input.data('propClearOnSave', next)
      if (next) {
        $input.val('')
      }
      if (opts.markDirty) {
        $input.data('dirty', true)
      }
      fnSyncBtnState()
      $input.trigger('change')
    })
    fnSyncBtnState()
    $input.data('clearToggleAttached', true)
  }

  const fnAttachProjectFilenameRefreshIfNeeded = function ($inputWrap, $input, path) {
    if (String(path || '').toLowerCase() !== NON_EXPORT_PROJECT_FILENAME_PATH) return
    if ($input.data('projectFilenameRefreshAttached') === true) return
    const normalize = (typeof opts.normalizeProjectFileName === 'function')
      ? opts.normalizeProjectFileName
      : function (raw) { return String(raw || '').trim() }
    let $group = $input.closest('.input-group')
    if (!$group.length) {
      $group = $('<div class="input-group input-group-sm"></div>')
      $input.before($group)
      $group.append($input)
    }
    const $btn = $('<button type="button" class="btn btn-outline-secondary" data-prop-project-filename-refresh></button>')
      .attr('title', fnT('project.export_filename_refresh'))
      .attr('data-bs-toggle', 'tooltip')
      .attr('data-bs-placement', 'top')
      .html('<i class="fa-solid fa-rotate-right"></i>')
    $group.prepend($btn)
    $btn.on('click', async function () {
      if ($input.prop('disabled')) return
      if (typeof opts.refreshProjectFileName !== 'function') return
      const $self = $(this)
      $self.prop('disabled', true)
      try {
        const next = await opts.refreshProjectFileName($input.val())
        const normalized = normalize(next)
        $input.val(normalized)
        $input.trigger('input')
      } finally {
        $self.prop('disabled', false)
      }
    })
    $input.data('projectFilenameRefreshAttached', true)
  }

  const fnAttachPointHeightToolIfNeeded = function ($inputWrap, $input, path) {
    const pathLower = String(path || '').toLowerCase()
    if (!pointTargets.length) return
    if (pathLower === NON_EXPORT_POINT_HEIGHT_PATH) return
    const isHeightTarget = pathLower.includes('executeheight') || /(^|[.:])height$/.test(pathLower)
    if (!isHeightTarget) return
    if ($input.data('pointHeightToolAttached') === true) return
    let $group = $input.closest('.input-group')
    if (!$group.length) {
      $group = $('<div class="input-group input-group-sm"></div>')
      $input.before($group)
      $group.append($input)
    }
    const menuId = (String($input.attr('id') || '') + '-height-tool').replace(/[^a-zA-Z0-9_-]/g, '-')
    const $dropdownWrap = $('<div class="input-group-text p-0 border-0 bg-transparent"></div>')
    const $dropdown = $('<div class="btn-group btn-group-sm"></div>')
    const $toggle = $('<button type="button" class="btn btn-outline-secondary dropdown-toggle" data-prop-height-tool-toggle data-bs-toggle="dropdown" aria-expanded="false"></button>')
      .attr('title', fnT('height_tool.dropdown_title'))
      .attr('data-bs-toggle', 'dropdown')
      .attr('data-bs-auto-close', 'true')
      .attr('aria-label', fnT('height_tool.dropdown_title'))
      .html('<i class="fa-solid fa-mountain-sun"></i>')
    const $menu = $('<ul class="dropdown-menu"></ul>').attr('id', menuId)
    const providerKeys = fnBuildEffectiveDemProviderKeys()
    providerKeys.forEach((providerKey) => {
      const providerLabel = fnGetDemProviderLabel(providerKey)
      const $btn = $('<button type="button" class="dropdown-item" data-prop-height-tool-provider></button>')
        .attr('data-prop-height-tool-provider', providerKey)
        .text(providerKey + ' - ' + providerLabel)
      const $li = $('<li></li>').append($btn)
      $menu.append($li)
    })
    $dropdown.append($toggle, $menu)
    $dropdownWrap.append($dropdown)
    $group.prepend($dropdownWrap)
    $menu.on('click', '[data-prop-height-tool-provider]', function () {
      const providerKey = String($(this).data('prop-height-tool-provider') || '').trim()
      if (!providerKey) return
      void (async function () {
        const ok = await fnRunPointHeightDemOverwriteFlow({
          points: pointTargets,
          targetPath: path,
          providerKey: providerKey,
          onApplied: opts.onPointHeightToolApplied
        })
        if (!ok) return
        const fieldMeta = fnGetPointHeightToolFieldMeta(path)
        const values = pointTargets.map((pt) => {
          if (!pt) return null
          if (fieldMeta && Array.isArray(fieldMeta.aliases) && fieldMeta.aliases.length) {
            return fnGetPointNumericByPathAliases(pt, fieldMeta.aliases)
          }
          return toFiniteNumber(_.get(pt, 'props.' + path))
        }).filter((n) => Number.isFinite(Number(n)))
        const hasUniformValue = values.length === pointTargets.length && values.every((n) => Number(n) === Number(values[0]))
        if (hasUniformValue) {
          $input.val(String(values[0]))
        } else {
          $input.val('')
        }
        $input.data('dirty', false)
      })()
    })
    $input.data('pointHeightToolAttached', true)
  }

  const fnNormalizeRowPathForRender = function (rawPath) {
    return String(rawPath || '')
      .toLowerCase()
      .replace(/wpml:/g, '')
      .replace(/\.\d+(?=\.|$)/g, '')
      .trim()
  }
  const fnDisplayPathPart = function (partRaw) {
    const raw = String(partRaw || '')
    if (!opts.stripNamespacePrefixes) return raw
    return raw.replace(/^[a-zA-Z_][\w-]*:/, '')
  }
  const rowsToRender = Array.isArray(rows) ? rows.slice() : []
  const templateGimbalPitchIndex = rowsToRender.findIndex((item) => {
    const normalized = fnNormalizeRowPathForRender(item && item.path)
    if (!normalized) return false
    if (normalized.includes('waypointgimbalheadingparam')) return false
    return normalized === 'gimbalpitchangle' || normalized.endsWith('.gimbalpitchangle')
  })
  const firstWaypointGimbalGroupIndex = rowsToRender.findIndex((item) => {
    const normalized = fnNormalizeRowPathForRender(item && item.path)
    return !!normalized && normalized.includes('waypointgimbalheadingparam.')
  })
  if (templateGimbalPitchIndex !== -1 && firstWaypointGimbalGroupIndex !== -1 && templateGimbalPitchIndex !== (firstWaypointGimbalGroupIndex - 1)) {
    const templateRow = rowsToRender.splice(templateGimbalPitchIndex, 1)[0]
    const targetIndex = rowsToRender.findIndex((item) => {
      const normalized = fnNormalizeRowPathForRender(item && item.path)
      return !!normalized && normalized.includes('waypointgimbalheadingparam.')
    })
    if (targetIndex !== -1) {
      rowsToRender.splice(targetIndex, 0, templateRow)
    } else {
      rowsToRender.push(templateRow)
    }
  }

  let lastPrefix = null
  rowsToRender.forEach((row) => {
    const value = (row.value === null || row.value === undefined) ? '' : String(row.value)
    const inputId = (opts.idPrefix + '-' + row.path).replace(/[^a-zA-Z0-9_-]/g, '-')
    const pathParts = String(row.path || '').split('.')
    const displayParts = pathParts.map(fnDisplayPathPart)
    const forceFullPathLabel = String(row.path || '').toLowerCase() === NON_EXPORT_POINT_HEIGHT_PATH
    const rowPrefix = forceFullPathLabel
      ? ''
      : (displayParts.length > 1 ? displayParts.slice(0, -1).join('.') : '')
    const normalizedRowPrefix = String(rowPrefix || '').trim().toLowerCase()
    const showSharedPrefix = opts.compactSharedPrefix && rowPrefix && normalizedRowPrefix !== 'project'
    const rowLabel = (opts.compactSharedPrefix && displayParts.length > 1 && !forceFullPathLabel) ? displayParts[displayParts.length - 1] : displayParts.join('.')
    if (showSharedPrefix && rowPrefix !== lastPrefix) {
      const $prefixGroup = $('<div class="row"></div>')
      const $prefixLabel = $('<div class="col-12 small fw-semibold text-muted mt-2 mb-1"></div>').text(rowPrefix)
      $prefixGroup.append($prefixLabel)
      $groupsParent.append($prefixGroup)
      lastPrefix = rowPrefix
    } else if (!showSharedPrefix && normalizedRowPrefix === 'project') {
      lastPrefix = null
    }
    const $group = $('<div class="mb-2 row align-items-center"></div>')
    const $labelWrap = $('<label class="col-sm-5 col-form-label col-form-label-sm d-inline-flex align-items-center"></label>').attr('for', inputId)
    const $inputWrap = $('<div class="col-sm-7"></div>')
    const $input = $('<input type="text" class="form-control form-control-sm">').attr('id', inputId).val(value)
    $input
      .data('prop-path', row.path)
      .data('prop-type', typeof row.value)
      .attr('data-prop-path', String(row.path || ''))
      .attr('data-prop-type', String(typeof row.value || 'string'))
    if (opts.readonlyPaths.includes(row.path) && String(row.path || '').toLowerCase() !== 'point.coordinates') {
      $input.prop('disabled', true).attr('aria-disabled', 'true')
    }
    if (opts.markDirty) {
      $input.data('dirty', false)
    }
    const hasDefaultForPath = Object.prototype.hasOwnProperty.call(templateDefaults, row.path)
    const defaultValueRaw = hasDefaultForPath ? templateDefaults[row.path] : null
    const defaultValue = (defaultValueRaw === null || defaultValueRaw === undefined) ? '' : String(defaultValueRaw)
    const isActionsSecondaryText = row.path === 'ExtendedData.mis:actions.1'
    const rowPathNoNs = pathWithoutNamespacePrefixes(row.path)
    const propPresetOptions = fnResolvePropPresetOptions(row.path, rowPathNoNs, row.value)
    const leafField = getPathLeafField(pathWithoutLeadingScope(rowPathNoNs))
    const isTimestampField = leafField === 'createTime' || leafField === 'updateTime'
    const rowLabelTooltip = fnResolvePropLabelTooltip(row.path, rowPathNoNs)
    const rowLabelDetailedHelp = fnResolvePropLabelDetailedHelp(row.path, rowPathNoNs, rowLabelTooltip)
    const rowLabelIconClass = fnResolvePropLabelIconClass(row.path, rowPathNoNs)
    const rowLabelRequired = fnResolvePropLabelRequired(row.path, rowPathNoNs)
    const rowKmzScopeInfo = fnResolveKmzScopeInfo(row.path)
    const rowScopeHelp = fnBuildKmzScopeContextHelp(rowLabelTooltip, rowLabelDetailedHelp, rowKmzScopeInfo)
    const $labelIcon = $('<i class="me-1"></i>').addClass(rowLabelIconClass).attr('aria-hidden', 'true')
    const $labelText = $('<span></span>').text(rowLabel)
    if (rowLabelRequired) {
      $labelWrap.addClass('text-danger')
      $labelIcon.addClass('text-danger')
    }
    if (rowKmzScopeInfo) {
      $labelIcon
        .attr('data-prop-scope-icon', '1')
        .attr('data-prop-scope-template', String(rowKmzScopeInfo.template))
        .attr('data-prop-scope-waylines', String(rowKmzScopeInfo.waylines))
        .attr('data-prop-scope-unresolved', String(rowKmzScopeInfo.unresolved))
    }
    $labelWrap.append($labelIcon).append($labelText)
    $labelWrap.on('contextmenu', function (event) {
      fnHandlePropContextMenu(event, {
        label: rowLabel,
        fullName: String(row.path || ''),
        helpShort: rowScopeHelp.shortHelp,
        helpDetailed: rowScopeHelp.detailedHelp,
        kmzScopeVariant: rowScopeHelp.kmzScopeVariant
      })
    })
    const tooltipHtmlParts = []
    if (rowLabelTooltip) {
      tooltipHtmlParts.push(escapePropTooltipHtml(rowLabelTooltip))
    }
    if (rowKmzScopeInfo) {
      tooltipHtmlParts.push(escapePropTooltipHtml(fnBuildKmzScopeTooltip(rowKmzScopeInfo)))
    }
    if (tooltipHtmlParts.length) {
      $labelIcon
        .css('cursor', 'help')
        .removeAttr('title')
        .attr('data-bs-toggle', 'tooltip')
        .attr('data-bs-placement', 'top')
        .attr('data-bs-html', 'true')
        .attr('data-bs-title', tooltipHtmlParts.join('<br>'))
    }
    const canApplyDefaultButton = (row.path.endsWith('.@param') || row.path.endsWith('._text') || isActionsSecondaryText) &&
      hasDefaultForPath && defaultValue !== '' && !opts.readonlyPaths.includes(row.path)
    if (isTimestampField && !opts.readonlyPaths.includes(row.path)) {
      $input.addClass('d-none')
      $inputWrap.append($input)
      const $groupWrap = $('<div></div>')
      const $displayGroup = $('<div class="input-group input-group-sm"></div>')
      const $editRawBtn = $('<button type="button" class="btn btn-outline-secondary"></button>')
        .attr('data-prop-timestamp-edit', '1')
        .html('<i class="fa-solid fa-pen"></i>')
      .attr('title', fnT('ui.edit_raw_unix'))
        .attr('data-bs-toggle', 'tooltip')
        .attr('data-bs-placement', 'top')
      const $displayInput = $('<input type="text" class="form-control form-control-sm">')
        .attr('placeholder', TIMESTAMP_DISPLAY_FORMAT)
      const $rawInput = $('<input type="text" class="form-control form-control-sm d-none mt-1">')
        .attr('data-prop-timestamp-raw', '1')
        .attr('placeholder', 'UNIX ms (pl. 1764852201000)')
      const fnSyncFromRaw = function (rawVal, triggerInput) {
        const unixMs = toUnixMs(rawVal)
        const normalizedRaw = (unixMs === null) ? '' : String(unixMs)
        $rawInput.val(normalizedRaw)
        $displayInput.val(fnFormatUnixMsForDisplay(normalizedRaw))
        $displayInput.removeClass('is-invalid')
        $rawInput.removeClass('is-invalid')
        $input.val(normalizedRaw)
        if (triggerInput) $input.trigger('input')
      }
      const fnSyncFromDisplay = function (displayVal) {
        const unixMs = fnParseDisplayTimestampToUnixMs(displayVal)
        if (unixMs === null) {
          $displayInput.addClass('is-invalid')
          return
        }
        $displayInput.removeClass('is-invalid')
        fnSyncFromRaw(unixMs, true)
      }
      $displayGroup.append($editRawBtn).append($displayInput)
      $groupWrap.append($displayGroup).append($rawInput)
      $inputWrap.append($groupWrap)
      fnSyncFromRaw($input.val(), false)
      $displayInput.on('change blur', function () {
        fnSyncFromDisplay($displayInput.val())
      })
      $rawInput.on('input', function () {
        const text = String($rawInput.val() || '').trim()
        if (!text) {
          fnSyncFromRaw('', true)
          return
        }
        if (!/^-?\d+$/.test(text)) {
          $rawInput.addClass('is-invalid')
          return
        }
        $rawInput.removeClass('is-invalid')
        fnSyncFromRaw(text, true)
      })
      $editRawBtn.on('click', function () {
        if ($input.prop('disabled')) return
        const hidden = $rawInput.hasClass('d-none')
        $rawInput.toggleClass('d-none', !hidden)
        if (hidden) $rawInput.trigger('focus')
      })
      fnAttachClearToggleIfNeeded($inputWrap, $input)
    } else if (row.path === 'ExtendedData.mis:heading') {
      const suggestModel = fnBuildHeadingSuggestModel()
      const $templateRoot = ($container && $container.length && $container.get(0) && $container.get(0).ownerDocument)
        ? $($container.get(0).ownerDocument.body)
        : $(document.body)
      const tpl = String($templateRoot.find('[data-tpl-heading-suggest-buttons]').first().html() || '').trim()
      if (suggestModel && tpl) {
        const $buttonsWrap = $('<div></div>').append($(tpl))
        const $buttons = $buttonsWrap.find('[data-heading-suggest-btn]')
        if ($buttons.length >= suggestModel.length) {
          suggestModel.forEach((item, idx) => {
            const $btn = $buttons.eq(idx)
            $btn.attr('data-heading-suggest', item.value)
            $btn.find('[data-heading-prefix]').text(item.prefix)
            $btn.find('[data-heading-value]').text(item.value)
          })
          const $inputGroup = $('<div class="input-group input-group-sm"></div>')
          $inputGroup.append($input)
          $inputGroup.append($buttons)
          $inputGroup.on('click', '[data-heading-suggest]', function () {
            const val = $(this).data('heading-suggest')
            $input.val(String(val))
            $input.trigger('input')
          })
          $inputWrap.append($inputGroup)
          fnAttachClearToggleIfNeeded($inputWrap, $input)
        } else {
          $inputWrap.append($input)
          fnAttachClearToggleIfNeeded($inputWrap, $input)
        }
      } else {
        $inputWrap.append($input)
        fnAttachClearToggleIfNeeded($inputWrap, $input)
      }
    } else if (propPresetOptions && !opts.readonlyPaths.includes(row.path)) {
      $input.addClass('d-none')
      $inputWrap.append($input)
      const $presetWrap = $('<div class="d-flex flex-wrap gap-1 mt-1"></div>')
      const $editBtn = $('<button type="button" class="badge fw-normal text-bg-secondary border-0 py-1 px-2" data-prop-preset-edit></button>')
        .css('cursor', 'pointer')
        .html('<i class="fa-solid fa-pen"></i>')
        .attr('title', fnT('action.custom_value_edit'))
        .attr('data-bs-toggle', 'tooltip')
        .attr('data-bs-placement', 'top')
      $presetWrap.append($editBtn)
      let $presetButtons = $()
      propPresetOptions.forEach((opt) => {
        const modeValue = String(opt && opt.value !== undefined ? opt.value : '').trim()
        if (!modeValue) return
        const modeLabel = String(opt && opt.label !== undefined ? opt.label : modeValue).trim() || modeValue
        const isDefaultPreset = !!(opt && opt.default === true)
        const tooltipRaw = fnResolveI18Text(String(opt && opt.tooltip !== undefined ? opt.tooltip : '').trim(), '')
        const defaultTooltipSuffix = isDefaultPreset ? (' ' + String(fnT('preset.default_suffix') || '').trim()) : ''
        const tooltip = (tooltipRaw || ('Mezoertek: ' + modeValue)) + defaultTooltipSuffix
        const valueDetailedHelp = fnResolvePropValueHelp(row.path, rowPathNoNs, modeValue, tooltip)
        const $btn = $('<button type="button" class="badge fw-normal border border-secondary bg-light text-dark py-1 px-2" data-prop-preset-apply></button>')
          .attr('data-prop-preset-value', modeValue)
          .css('cursor', 'pointer')
          .html(isDefaultPreset
            ? ('<i class="fa-solid fa-asterisk"></i> ' + modeLabel)
            : modeLabel)
        $btn
          .attr('title', tooltip)
          .attr('data-bs-toggle', 'tooltip')
          .attr('data-bs-placement', 'top')
        $btn.on('contextmenu', function (event) {
          const valueScopeHelp = fnBuildKmzScopeContextHelp(tooltip, valueDetailedHelp, rowKmzScopeInfo)
          fnHandlePropContextMenu(event, {
            label: rowLabel,
            fullName: String(row.path || ''),
            helpShort: valueScopeHelp.shortHelp,
            helpDetailed: valueScopeHelp.detailedHelp,
            kmzScopeVariant: valueScopeHelp.kmzScopeVariant
          })
        })
        $presetWrap.append($btn)
        $presetButtons = $presetButtons.add($btn)
      })
      const fnSyncPresetActive = function () {
        const current = String($input.val() || '').trim()
        $presetButtons.each(function () {
          const $btn = $(this)
          const rawBtnValue = $btn.data('propPresetValue')
          const v = String((rawBtnValue === undefined || rawBtnValue === null) ? '' : rawBtnValue).trim()
          const isActive = !!v && v === current
          $btn.toggleClass('bg-primary', isActive)
          $btn.toggleClass('text-white', isActive)
          $btn.toggleClass('bg-light', !isActive)
          $btn.toggleClass('text-dark', !isActive)
        })
      }
      const fnSyncPresetInteractable = function () {
        const disabled = !!$input.prop('disabled')
        $presetWrap.find('[data-prop-preset-edit], [data-prop-preset-apply]').prop('disabled', disabled)
      }
      $presetWrap.on('click', '[data-prop-preset-edit]', function () {
        if ($input.prop('disabled')) return
        const isHidden = $input.hasClass('d-none')
        $input.toggleClass('d-none', !isHidden)
        if (isHidden) {
          $input.trigger('focus')
        }
      })
      $presetWrap.on('click', '[data-prop-preset-apply]', function () {
        if ($input.prop('disabled')) return
        const rawModeValue = $(this).data('propPresetValue')
        const modeValue = String((rawModeValue === undefined || rawModeValue === null) ? '' : rawModeValue).trim()
        if (!modeValue) return
        $input.val(modeValue)
        $input.trigger('input')
      })
      $input.on('input', fnSyncPresetActive)
      fnSyncPresetActive()
      fnSyncPresetInteractable()
      $inputWrap.append($presetWrap)
      fnAttachClearToggleIfNeeded($inputWrap, $input)
    } else if (canApplyDefaultButton) {
      const $inputGroup = $('<div class="input-group input-group-sm"></div>')
      const $applyDefault = $('<button type="button" class="btn btn-outline-secondary" data-prop-default-apply></button>')
        .text(defaultValue)
      $inputGroup.append($input).append($applyDefault)
      $inputGroup.on('click', '[data-prop-default-apply]', function () {
        if ($input.prop('disabled')) return
        $input.val(defaultValue)
        $input.trigger('input')
      })
      if ($input.prop('disabled')) {
        $applyDefault.prop('disabled', true)
      }
      $inputWrap.append($inputGroup)
      fnAttachClearToggleIfNeeded($inputWrap, $input)
    } else {
      $inputWrap.append($input)
      fnAttachClearToggleIfNeeded($inputWrap, $input)
    }
    fnAttachProjectFilenameRefreshIfNeeded($inputWrap, $input, row.path)
    fnAttachPointHeightToolIfNeeded($inputWrap, $input, row.path)
    const pathHelpText = String(helpTextByPath[row.path] || '').trim()
    if (pathHelpText) {
      $inputWrap.append($('<div class="form-text"></div>').text(pathHelpText))
    }
    $group.append($labelWrap).append($inputWrap)
    $groupsParent.append($group)
  })
  const fnToBoolLike = function (value) {
    const raw = String(value === null || value === undefined ? '' : value).trim().toLowerCase()
    if (raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on') return true
    const n = Number(raw)
    if (Number.isFinite(n) && n > 0) return true
    return false
  }
  const fnToCanonicalPath = function (path) {
    return pathWithoutNumericParts(pathWithoutLeadingScope(pathWithoutNamespacePrefixes(path)))
  }
  const fnShouldDisableByDependency = function (targetPath, values) {
    const canonicalPath = fnToCanonicalPath(targetPath)
    const leafField = getPathLeafField(canonicalPath)
    const fnResolveScopedControllerValue = function (controller) {
      const controllerName = String(controller || '').trim()
      if (!controllerName) return undefined
      const suffix = '.' + controllerName
      let bestValue = undefined
      let bestPrefixLen = -1
      Object.keys(values).forEach((key) => {
        const candidate = String(key || '')
        if (!candidate.endsWith(suffix)) return
        const prefix = candidate.slice(0, -suffix.length)
        if (!prefix) return
        if (!(canonicalPath === prefix || canonicalPath.indexOf(prefix + '.') === 0)) return
        if (prefix.length > bestPrefixLen) {
          bestPrefixLen = prefix.length
          bestValue = values[key]
        }
      })
      if (bestValue !== undefined) return bestValue
      if (Object.prototype.hasOwnProperty.call(values, controllerName)) {
        return values[controllerName]
      }
      return undefined
    }
    const fnControllerMatches = function (rule) {
      const rawControllerVal = fnResolveScopedControllerValue(rule && rule.controller)
      const mode = String((rule && rule.when) || 'truthy').trim().toLowerCase()
      if (mode === 'falsy') return !fnToBoolLike(rawControllerVal)
      if (mode === 'equals') {
        const expected = Array.isArray(rule && rule.equals) ? rule.equals : [rule && rule.equals]
        const normalized = String(rawControllerVal === undefined || rawControllerVal === null ? '' : rawControllerVal).trim()
        return expected.some((v) => normalized === String(v === undefined || v === null ? '' : v).trim())
      }
      return fnToBoolLike(rawControllerVal)
    }
    const fnTargetMatches = function (target) {
      const type = String((target && target.type) || 'leaf').trim().toLowerCase()
      const value = fnToCanonicalPath(String((target && target.value) || '').trim())
      if (!value) return false
      if (type === 'path') return canonicalPath === value
      if (type === 'prefix') return canonicalPath === value || canonicalPath.indexOf(value + '.') === 0
      if (type === 'contains') return canonicalPath.indexOf(value) !== -1
      const targetLeaf = getPathLeafField(value)
      return !!targetLeaf && targetLeaf === leafField
    }
    for (let i = 0; i < PROP_FIELD_DISABLE_RULES.length; i++) {
      const rule = PROP_FIELD_DISABLE_RULES[i]
      const controller = String(rule && rule.controller || '').trim()
      if (!controller) continue
      if (leafField === controller) continue
      if (!fnControllerMatches(rule)) continue
      const targets = Array.isArray(rule && rule.targets) ? rule.targets : []
      if (!targets.length) continue
      if (targets.some(fnTargetMatches)) {
        return true
      }
    }
    return false
  }
  const fnApplyDynamicDisabledState = function () {
    const values = {}
    const $inputsAll = $groupsParent.find('input[data-prop-path]')
    $inputsAll.each(function () {
      const $rowInput = $(this)
      const path = String($rowInput.data('prop-path') || '')
      const canonicalPath = fnToCanonicalPath(path)
      const leaf = getPathLeafField(canonicalPath)
      const val = String($rowInput.val() || '').trim()
      if (leaf) values[leaf] = val
      values[canonicalPath] = val
    })
    $inputsAll.each(function () {
      const $rowInput = $(this)
      const rowPath = String($rowInput.data('prop-path') || '')
      const isPointCoordinatesPath = String(rowPath || '').toLowerCase() === 'point.coordinates'
      const hardDisabled = isPointCoordinatesPath ? false : opts.readonlyPaths.includes(rowPath)
      const depDisabled = isPointCoordinatesPath ? false : fnShouldDisableByDependency(rowPath, values)
      const disabled = !!(hardDisabled || depDisabled)
      $rowInput.prop('disabled', disabled)
      if (disabled) {
        $rowInput.attr('aria-disabled', 'true')
      } else {
        $rowInput.removeAttr('aria-disabled')
      }
      const $controlsWrap = $rowInput.closest('.col-sm-7')
      $controlsWrap.find('[data-prop-preset-edit], [data-prop-preset-apply], [data-prop-default-apply], [data-prop-clear-toggle], [data-prop-timestamp-edit], [data-prop-timestamp-raw], [data-prop-project-filename-refresh], [data-prop-height-tool-toggle], [data-prop-height-tool-provider]').prop('disabled', disabled)
    })
  }
  fnApplyDynamicDisabledState()
  $groupsParent.on('input change', 'input[data-prop-path]', function () {
    fnApplyDynamicDisabledState()
  })
  $container.append($groupsParent)
  return $groupsParent.find('input')
}

const buildPropFieldResolvers = function (presets, {
  _,
  fnT,
  fnTF,
  fnResolveI18Text,
  pathWithoutLeadingScope,
  pathWithoutNumericParts,
  getPathLeafField
}) {
  const presetsRuntime = (presets.runtime && typeof presets.runtime === 'object') ? presets.runtime : {}
  const PROP_FIELD_VALUE_PRESETS = (presets.valuePresetsByPath && typeof presets.valuePresetsByPath === 'object') ? presets.valuePresetsByPath : {}
  const PROP_FIELD_VALUE_PRESETS_BY_FIELD = (presets.valuePresetsByField && typeof presets.valuePresetsByField === 'object') ? presets.valuePresetsByField : {}
  const PROP_FIELD_HELP_DETAILS_BY_FIELD = (presetsRuntime.fieldHelpDetailsByField && typeof presetsRuntime.fieldHelpDetailsByField === 'object') ? presetsRuntime.fieldHelpDetailsByField : {}
  const PROP_VALUE_HELP_BY_PATH = (presetsRuntime.valueHelpByPath && typeof presetsRuntime.valueHelpByPath === 'object') ? presetsRuntime.valueHelpByPath : {}
  const PROP_VALUE_HELP_BY_FIELD = (presetsRuntime.valueHelpByField && typeof presetsRuntime.valueHelpByField === 'object') ? presetsRuntime.valueHelpByField : {}
  const PROP_FIELD_TOOLTIPS = (presetsRuntime.fieldTooltipsByPath && typeof presetsRuntime.fieldTooltipsByPath === 'object') ? presetsRuntime.fieldTooltipsByPath : {}
  const PROP_FIELD_TOOLTIPS_BY_FIELD = (presetsRuntime.fieldTooltipsByField && typeof presetsRuntime.fieldTooltipsByField === 'object') ? presetsRuntime.fieldTooltipsByField : {}
  const PROP_FIELD_ICONS = (presetsRuntime.fieldIconsByPath && typeof presetsRuntime.fieldIconsByPath === 'object') ? presetsRuntime.fieldIconsByPath : {}
  const PROP_FIELD_ICONS_BY_FIELD = (presetsRuntime.fieldIconsByField && typeof presetsRuntime.fieldIconsByField === 'object') ? presetsRuntime.fieldIconsByField : {}
  const PROP_FIELD_REQUIRED = (presetsRuntime.fieldRequiredByPath && typeof presetsRuntime.fieldRequiredByPath === 'object') ? presetsRuntime.fieldRequiredByPath : {}
  const PROP_FIELD_REQUIRED_BY_FIELD = (presetsRuntime.fieldRequiredByField && typeof presetsRuntime.fieldRequiredByField === 'object') ? presetsRuntime.fieldRequiredByField : {}

  const fnResolvePropPresetOptions = function (path, pathNoNs, currentValue) {
    const variants = _.uniq([
      String(path || ''),
      String(pathNoNs || ''),
      pathWithoutLeadingScope(pathNoNs),
      pathWithoutNumericParts(pathNoNs),
      pathWithoutNumericParts(pathWithoutLeadingScope(pathNoNs))
    ].filter(Boolean))
    for (let i = 0; i < variants.length; i++) {
      const candidate = variants[i]
      if (Array.isArray(PROP_FIELD_VALUE_PRESETS[candidate]) && PROP_FIELD_VALUE_PRESETS[candidate].length) {
        return PROP_FIELD_VALUE_PRESETS[candidate]
      }
    }
    const leafField = getPathLeafField(pathWithoutLeadingScope(pathNoNs))
    if (leafField && Array.isArray(PROP_FIELD_VALUE_PRESETS_BY_FIELD[leafField]) && PROP_FIELD_VALUE_PRESETS_BY_FIELD[leafField].length) {
      return PROP_FIELD_VALUE_PRESETS_BY_FIELD[leafField]
    }
    const normalizedLeaf = String(leafField || '').trim()
    if (normalizedLeaf) {
      const boolLikeByName = /^(use|is)[A-Z]/.test(normalizedLeaf) || /Enable$/i.test(normalizedLeaf)
      if (boolLikeByName) {
        return [
          { value: '0', label: '0', tooltip: 'Kikapcsolva (false)' },
          { value: '1', label: '1', tooltip: 'Bekapcsolva (true)' }
        ]
      }
    }
    return null
  }

  const fnResolvePropLabelTooltip = function (path, pathNoNs) {
    const variants = _.uniq([
      String(path || ''),
      String(pathNoNs || ''),
      pathWithoutLeadingScope(pathNoNs),
      pathWithoutNumericParts(pathNoNs),
      pathWithoutNumericParts(pathWithoutLeadingScope(pathNoNs))
    ].filter(Boolean))
    for (let i = 0; i < variants.length; i++) {
      const candidate = variants[i]
      if (PROP_FIELD_TOOLTIPS[candidate]) {
        const resolved = fnResolveI18Text(PROP_FIELD_TOOLTIPS[candidate], '')
        if (resolved) return resolved
      }
    }
    const leafField = getPathLeafField(pathWithoutLeadingScope(pathNoNs))
    if (leafField && PROP_FIELD_TOOLTIPS_BY_FIELD[leafField]) {
      const resolved = fnResolveI18Text(PROP_FIELD_TOOLTIPS_BY_FIELD[leafField], '')
      if (resolved) return resolved
    }
    const fallbackPath = pathWithoutLeadingScope(pathNoNs)
    if (!fallbackPath) return ''
    return fnT('dji.field_prefix') + ' ' + fallbackPath
  }

  const fnNormalizeIconClass = function (iconClass) {
    const raw = String(iconClass || '').trim()
    if (!raw) return 'fa-solid fa-tag'
    const tokens = raw.split(/\s+/).map((t) => String(t || '').trim()).filter(Boolean)
    const safeTokens = tokens.filter((t) => /^[a-z0-9-]+$/i.test(t))
    const hasFaBase = safeTokens.some((t) => t.indexOf('fa-') === 0)
    if (!safeTokens.length || !hasFaBase) return 'fa-solid fa-tag'
    return safeTokens.join(' ')
  }

  const fnResolvePropLabelIconClass = function (path, pathNoNs) {
    const variants = _.uniq([
      String(path || ''),
      String(pathNoNs || ''),
      pathWithoutLeadingScope(pathNoNs),
      pathWithoutNumericParts(pathNoNs),
      pathWithoutNumericParts(pathWithoutLeadingScope(pathNoNs))
    ].filter(Boolean))
    for (let i = 0; i < variants.length; i++) {
      const candidate = variants[i]
      if (PROP_FIELD_ICONS[candidate]) return fnNormalizeIconClass(PROP_FIELD_ICONS[candidate])
    }
    const leafField = getPathLeafField(pathWithoutLeadingScope(pathNoNs))
    if (leafField && PROP_FIELD_ICONS_BY_FIELD[leafField]) {
      return fnNormalizeIconClass(PROP_FIELD_ICONS_BY_FIELD[leafField])
    }
    return 'fa-solid fa-tag'
  }

  const fnResolvePropLabelRequired = function (path, pathNoNs) {
    const variants = _.uniq([
      String(path || ''),
      String(pathNoNs || ''),
      pathWithoutLeadingScope(pathNoNs),
      pathWithoutNumericParts(pathNoNs),
      pathWithoutNumericParts(pathWithoutLeadingScope(pathNoNs))
    ].filter(Boolean))
    for (let i = 0; i < variants.length; i++) {
      const candidate = variants[i]
      if (Object.prototype.hasOwnProperty.call(PROP_FIELD_REQUIRED, candidate)) {
        return !!PROP_FIELD_REQUIRED[candidate]
      }
    }
    const leafField = getPathLeafField(pathWithoutLeadingScope(pathNoNs))
    if (leafField && Object.prototype.hasOwnProperty.call(PROP_FIELD_REQUIRED_BY_FIELD, leafField)) {
      return !!PROP_FIELD_REQUIRED_BY_FIELD[leafField]
    }
    return false
  }

  const fnResolvePropLabelDetailedHelp = function (path, pathNoNs, labelTooltip) {
    const leafField = getPathLeafField(pathWithoutLeadingScope(pathNoNs))
    if (leafField && PROP_FIELD_HELP_DETAILS_BY_FIELD[leafField]) {
      const raw = String(PROP_FIELD_HELP_DETAILS_BY_FIELD[leafField] || '')
      const resolved = fnResolveI18Text(raw, '')
      if (resolved) return resolved
    }
    const fallbackPath = pathWithoutLeadingScope(pathNoNs) || String(path || '')
    const shortHelp = String(labelTooltip || '').trim()
    if (shortHelp) {
      return shortHelp + ' ' + fnTF('dji.field_influence', { field: fallbackPath })
    }
    return fnTF('dji.field_direct_update', { field: fallbackPath })
  }

  const fnResolvePropValueHelp = function (path, pathNoNs, value, fallbackTooltip) {
    const variants = _.uniq([
      String(path || ''),
      String(pathNoNs || ''),
      pathWithoutLeadingScope(pathNoNs),
      pathWithoutNumericParts(pathNoNs),
      pathWithoutNumericParts(pathWithoutLeadingScope(pathNoNs))
    ].filter(Boolean))
    const val = String(value || '').trim()
    for (let i = 0; i < variants.length; i++) {
      const candidate = variants[i]
      const map = PROP_VALUE_HELP_BY_PATH[candidate]
      if (map && typeof map === 'object' && Object.prototype.hasOwnProperty.call(map, val)) {
        const raw = String(map[val] || '').trim()
        const resolved = fnResolveI18Text(raw, '')
        if (resolved) return resolved
        if (raw && !/^[a-z0-9_.-]+$/i.test(raw)) return raw
      }
    }
    const leafField = getPathLeafField(pathWithoutLeadingScope(pathNoNs))
    if (leafField && PROP_VALUE_HELP_BY_FIELD[leafField] && Object.prototype.hasOwnProperty.call(PROP_VALUE_HELP_BY_FIELD[leafField], val)) {
      const raw = String(PROP_VALUE_HELP_BY_FIELD[leafField][val] || '').trim()
      const resolved = fnResolveI18Text(raw, '')
      if (resolved) return resolved
      if (raw && !/^[a-z0-9_.-]+$/i.test(raw)) return raw
    }
    const tipRaw = String(fallbackTooltip || '').trim()
    const tip = fnResolveI18Text(tipRaw, '')
    if (!tip && tipRaw && !/^[a-z0-9_.-]+$/i.test(tipRaw)) return tipRaw
    if (tip) return tip
    const fallbackPath = pathWithoutLeadingScope(pathNoNs) || String(path || '')
    return fnTF('dji.value_set', { field: fallbackPath, value: val })
  }

  return {
    fnResolvePropPresetOptions,
    fnResolvePropLabelTooltip,
    fnNormalizeIconClass,
    fnResolvePropLabelIconClass,
    fnResolvePropLabelRequired,
    fnResolvePropLabelDetailedHelp,
    fnResolvePropValueHelp
  }
}

export {
  renderPropEditorRows,
  buildPropFieldResolvers
}
